// =============================================================
//  ManageAnnouncementsScreen — Gestion des annonces
// =============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  Button,
  Badge,
  EmptyState,
  Loading,
  DataRow,
  SegmentedSelect,
  SearchBar,
  StatCard,
} from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const STATUS_TABS = [
  { value: 'all', label: 'Toutes' },
  { value: 'active', label: 'Actives' },
  { value: 'pending', label: 'En attente' },
  { value: 'expired', label: 'Expirées' },
];

const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (typeof ts === 'number') return new Date(ts);
  return null;
};

const fmtDate = (ts) => {
  const d = toDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString('fr-FR');
};

export default function ManageAnnouncementsScreen({ navigation }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const ref = collection(db, 'announcements');
      let snap;
      try {
        snap = await getDocs(query(ref, orderBy('createdAt', 'desc')));
      } catch {
        snap = await getDocs(ref);
      }
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.log('announcements load error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    return {
      total: items.length,
      active: items.filter((a) => a.status === 'active').length,
      pending: items.filter((a) => a.status === 'pending').length,
    };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (!q) return true;
      return (a.title || a.titre || '').toLowerCase().includes(q);
    });
  }, [items, statusFilter, search]);

  const statusVariant = (s) => {
    if (s === 'active') return 'success';
    if (s === 'pending') return 'warning';
    if (s === 'expired') return 'neutral';
    if (s === 'rejected') return 'danger';
    return 'neutral';
  };

  return (
    <AdminLayout
      title="Gestion des annonces"
      subtitle="Annonces des centres et leur statut"
    >
      <View style={styles.statsRow}>
        <StatCard
          label="Total annonces"
          value={stats.total}
          icon="bullhorn-outline"
          accent={colors.primary}
          loading={loading}
        />
        <StatCard
          label="Actives"
          value={stats.active}
          icon="check-circle-outline"
          accent={colors.success}
          loading={loading}
        />
        <StatCard
          label="En attente"
          value={stats.pending}
          icon="clock-outline"
          accent={colors.warning}
          loading={loading}
        />
      </View>

      <Card style={{ marginBottom: spacing.lg }}>
        <SegmentedSelect options={STATUS_TABS} value={statusFilter} onChange={setStatusFilter} />
      </Card>

      <View style={styles.toolbar}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <SearchBar value={search} onChangeText={setSearch} placeholder="Rechercher..." />
        </View>
        <Button
          icon="shield-check-outline"
          variant="secondary"
          onPress={() => navigation.navigate('AdminVerificationAnnonceScreen')}
        >
          Vérifications
        </Button>
      </View>

      <Card noPadding>
        {loading ? (
          <Loading label="Chargement..." />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="bullhorn-outline"
            title="Aucune annonce"
            subtitle="Aucune annonce ne correspond aux filtres"
          />
        ) : (
          filtered.map((a, i) => {
            const title = a.title || a.titre || 'Sans titre';
            return (
              <DataRow
                key={a.id}
                last={i === filtered.length - 1}
                onPress={() =>
                  navigation.navigate('AnnouncementDetailScreen', { id: a.id, announcement: a })
                }
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    backgroundColor: colors.primarySoft,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text style={{ color: colors.primary, fontWeight: '700', fontSize: 16 }}>
                    {title.charAt(0).toUpperCase()}
                  </Text>
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.rowTitle} numberOfLines={1}>{title}</Text>
                  <Text style={styles.rowSubtitle} numberOfLines={1}>
                    {a.centerName || a.author || '—'} · {fmtDate(a.createdAt)}
                  </Text>
                </View>
                <Badge variant={statusVariant(a.status)}>{a.status || '—'}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="arrow-right"
                  onPress={() =>
                    navigation.navigate('AnnouncementDetailScreen', { id: a.id, announcement: a })
                  }
                />
              </DataRow>
            );
          })
        )}
      </Card>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg, marginBottom: spacing.lg },
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
});
